import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { MdClose } from '@teamworksdev/icons';
import { useTranslation } from 'react-i18next';
import { HubEventItem } from './HubEventItem';
import {
  formatMoreEventsDayLabel,
  type MoreEventsDayDialogState,
} from './moreEventsDayDialogUtils';
import { WorkoutEventItem } from './WorkoutEventItem';

type MoreEventsDayDialogProps = {
  state: MoreEventsDayDialogState | null;
  onClose: () => void;
  onHubEventClick: (eventId: string) => void;
  onWorkoutEventClick: (
    blockId: string,
    workoutId: string,
    sourceTeamId?: string,
  ) => void;
};

export function MoreEventsDayDialog({
  state,
  onClose,
  onHubEventClick,
  onWorkoutEventClick,
}: MoreEventsDayDialogProps) {
  const { t } = useTranslation();
  const dayLabel = state ? formatMoreEventsDayLabel(state.dateIso) : '';

  return (
    <Dialog
      open={state !== null}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        className: 'w-[460px] max-w-[calc(100%-32px)]',
      }}
    >
      <DialogTitle className="flex items-center justify-between gap-2 px-6 py-6">
        <Typography
          sx={{ typography: 'h6' }}
          component="h2"
          className="truncate pb-2"
        >
          {dayLabel}
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label={t('close')}
          className="shrink-0"
        >
          <MdClose size={24} aria-hidden="true" />
        </IconButton>
      </DialogTitle>
      {state && (
        <DialogContent className="flex min-h-0 flex-col gap-1 overflow-y-auto px-6 pb-6 pt-0">
          {state.events.map((event) => {
            if (event.extendedProps['kind'] === 'hub') {
              return (
                <HubEventItem
                  key={event.id}
                  title={event.title}
                  startDateTime={event.extendedProps['startDateTime'] as string}
                  timezone={event.extendedProps['timezone'] as string}
                  isAllDay={event.extendedProps['isAllDay'] as boolean}
                  onClick={() => {
                    onHubEventClick(event.id);
                    onClose();
                  }}
                />
              );
            }

            // Cross-team events carry sourceTeamId (undefined for the active
            // team); onWorkoutEventClick handles both.
            return (
              <WorkoutEventItem
                key={event.id}
                title={event.title}
                primaryColor={event.extendedProps['primaryColor'] as string}
                backgroundColor={
                  event.extendedProps['backgroundColor'] as string
                }
                onClick={() => {
                  onWorkoutEventClick(
                    event.extendedProps['blockId'] as string,
                    event.extendedProps['workoutId'] as string,
                    event.extendedProps['sourceTeamId'] as string | undefined,
                  );
                  onClose();
                }}
              />
            );
          })}
        </DialogContent>
      )}
    </Dialog>
  );
}
